// Cashback — Pulsar Points loyalty asset.
//
// Per €1 of qualifying spend in a category, Pulsar issues:
//    pointsEarned = tier.cashbackRate × points.earnRateByCategory[category]
//
// Each point has face value `points.pointFaceValue` (default €0.01).
//
// Real cost to Pulsar per earned point depends on:
//   - which redemption path users pick (mix-weighted)
//   - cost-per-point of each path
//   - breakage (X% never redeemed → real cost factor = 1 - breakageRate)
//
// Default blended real cost ≈ 0.30 of face value. So a "1% headline" cashback
// costs ~0.30% on P&L, not 1%. That's the lever Q3 trades on.

/**
 * Returns the blended cost per point earned (EUR), accounting for redemption
 * mix and breakage. Pure function of inputs.points.
 */
export function blendedCostPerPoint(points) {
  const mix = points.redemptionMix;
  const cost = points.costPerRedemption;
  const blendedRedeemedCost =
    mix.subWaiver * cost.subWaiver +
    mix.marketplace * cost.marketplace +
    mix.crypto * cost.crypto +
    mix.usdCash * cost.usdCash;
  // Multiply by (1 - breakage): unused points cost nothing.
  return blendedRedeemedCost * (1 - points.breakageRate);
}

/**
 * Realistic spend-distribution-weighted cashback multiplier. Most card spend
 * lands in default/in-store (1×); a smaller share is online (3×); a niche
 * is stablecoin-funded (2×). ATM / gambling / taxes are excluded entirely.
 *
 * Defaults reflect typical European cardholder behavior. Schema bump can
 * surface this distribution as `points.spendDistribution` later if needed.
 */
const SPEND_DISTRIBUTION = {
  online: 0.18,
  inStore: 0.55,
  stablecoinFunded: 0.05,
  default: 0.22,
  // 0× categories (atm, gambling, taxes) excluded — they don't earn points
};

function realisticCategoryBlend(earnRateByCategory) {
  let blend = 0;
  for (const [cat, share] of Object.entries(SPEND_DISTRIBUTION)) {
    const rate = earnRateByCategory[cat] ?? earnRateByCategory.default ?? 1;
    blend += share * rate;
  }
  return blend;
}

/**
 * Cashback line for a single tier in a single month, per active user.
 *
 * @param {object} inputs
 * @param {object} tier
 * @param {number} spendEur  active user's monthly card spend (EUR)
 * @returns {{ pointsEarned: number, faceValue: number, realCost: number }}
 *   pointsEarned: number of points issued this month
 *   faceValue:    EUR liability at face value (display only, not P&L)
 *   realCost:     EUR cost to Pulsar this month (this is the P&L line)
 */
export function cashbackPerUser(inputs, tier, spendEur) {
  const cap = tier.cashbackSpendCap;
  const qualifyingSpend = Math.min(spendEur, cap);
  const categoryBlend = realisticCategoryBlend(inputs.points.earnRateByCategory);
  const pointsPerEur = tier.cashbackRate * categoryBlend;
  const pointsEarned = qualifyingSpend * pointsPerEur;

  const faceValue = pointsEarned * inputs.points.pointFaceValue;
  const realCost = pointsEarned * blendedCostPerPoint(inputs.points);

  return { pointsEarned, faceValue, realCost };
}
